import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'wouter';
import { apiRequest } from '@/lib/queryClient';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { 
  Sparkles, 
  Plus, 
  Check,
  Crown,
  Palette,
  X
} from 'lucide-react';
import type { CartoonCharacter } from '@shared/schema';

interface CartoonCharacterSelectorProps {
  isEnabled: boolean;
  onCharacterSelected: (character: CartoonCharacter | null) => void;
  selectedCharacter: CartoonCharacter | null;
}

export function CartoonCharacterSelector({ 
  isEnabled, 
  onCharacterSelected,
  selectedCharacter 
}: CartoonCharacterSelectorProps) {
  const { user, hasActiveSubscription, isFounder } = useAuth();
  const isPremium = hasActiveSubscription || isFounder;
  
  // Fetch user's cartoon characters
  const { data: characters, isLoading } = useQuery({
    queryKey: ['/api/cartoon-characters'],
    queryFn: async () => {
      const response = await apiRequest('GET', '/api/cartoon-characters');
      const data = await response.json();
      return data.characters as CartoonCharacter[];
    },
    enabled: isEnabled && isPremium && !!user
  });

  // Check for pre-selected character from localStorage
  useEffect(() => {
    if (isEnabled && !selectedCharacter) {
      const stored = localStorage.getItem('selectedCartoonCharacter');
      if (stored) {
        try {
          const character = JSON.parse(stored);
          onCharacterSelected(character);
          // Clear it after use
          localStorage.removeItem('selectedCartoonCharacter');
        } catch (e) {
          console.error('Failed to parse stored character:', e);
        }
      }
    }
  }, [isEnabled, selectedCharacter, onCharacterSelected]);

  if (!isEnabled) {
    return null;
  }

  if (!isPremium) {
    return (
      <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <Crown className="w-8 h-8 text-amber-500 flex-shrink-0 mt-1" />
            <div className="flex-1">
              <h3 className="font-semibold text-amber-800 mb-2">
                Premium Feature: Custom Characters
              </h3>
              <p className="text-sm text-amber-700 mb-3">
                Create cartoon characters from photos and use them in your story illustrations!
              </p>
              <Link href="/subscribe">
                <Button size="sm" className="bg-gradient-to-r from-amber-500 to-orange-500 text-white">
                  <Sparkles className="w-4 h-4 mr-2" />
                  Upgrade to Premium
                </Button>
              </Link>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Palette className="w-5 h-5 text-purple-600" />
            Character Selection
          </h3>
          <div className="flex gap-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="flex-shrink-0">
                <Skeleton className="w-24 h-24 rounded-lg" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!characters || characters.length === 0) {
    return (
      <Card>
        <CardContent className="p-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Palette className="w-5 h-5 text-purple-600" />
            Use Custom Characters
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            Create cartoon characters to use in your story illustrations
          </p>
          <Link href="/characters">
            <Button variant="outline" size="sm">
              <Plus className="w-4 h-4 mr-2" />
              Create Characters
            </Button>
          </Link>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold flex items-center gap-2">
            <Palette className="w-5 h-5 text-purple-600" />
            Select Character for Illustrations
            {selectedCharacter && (
              <Badge variant="secondary" className="ml-2">
                <Check className="w-3 h-3 mr-1" />
                Selected
              </Badge>
            )}
          </h3>
          {selectedCharacter && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onCharacterSelected(null)}
            >
              <X className="w-4 h-4 mr-1" />
              Clear
            </Button>
          )}
        </div>

        {characters.length <= 4 ? (
          // Grid layout for few characters
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {characters.map((character) => (
              <button
                key={character.id}
                onClick={() => onCharacterSelected(
                  selectedCharacter?.id === character.id ? null : character
                )}
                className={`relative group transition-all ${
                  selectedCharacter?.id === character.id
                    ? 'ring-2 ring-purple-600 rounded-lg'
                    : ''
                }`}
              >
                <div className="aspect-square rounded-lg overflow-hidden bg-gradient-to-br from-purple-100 to-teal-100">
                  <img
                    src={character.thumbnailUrl || character.cartoonImageUrl}
                    alt={character.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform"
                  />
                </div>
                {selectedCharacter?.id === character.id && (
                  <div className="absolute top-2 right-2 bg-purple-600 text-white rounded-full p-1">
                    <Check className="w-3 h-3" />
                  </div>
                )}
                <p className="text-xs font-medium mt-2 text-center truncate">
                  {character.name}
                </p>
              </button>
            ))}
            <Link href="/characters">
              <button className="aspect-square rounded-lg border-2 border-dashed border-gray-300 hover:border-purple-400 transition-colors flex flex-col items-center justify-center">
                <Plus className="w-6 h-6 text-gray-400 mb-1" />
                <span className="text-xs text-gray-500">Add More</span>
              </button>
            </Link>
          </div>
        ) : (
          // Carousel for many characters
          <Carousel className="w-full">
            <CarouselContent className="-ml-2">
              {characters.map((character) => (
                <CarouselItem key={character.id} className="pl-2 basis-1/3 sm:basis-1/4 md:basis-1/5">
                  <button
                    onClick={() => onCharacterSelected(
                      selectedCharacter?.id === character.id ? null : character
                    )}
                    className={`relative group transition-all ${
                      selectedCharacter?.id === character.id
                        ? 'ring-2 ring-purple-600 rounded-lg'
                        : ''
                    }`}
                  >
                    <div className="aspect-square rounded-lg overflow-hidden bg-gradient-to-br from-purple-100 to-teal-100">
                      <img
                        src={character.thumbnailUrl || character.cartoonImageUrl}
                        alt={character.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform"
                      />
                    </div>
                    {selectedCharacter?.id === character.id && (
                      <div className="absolute top-2 right-2 bg-purple-600 text-white rounded-full p-1">
                        <Check className="w-3 h-3" />
                      </div>
                    )}
                    <p className="text-xs font-medium mt-2 text-center truncate">
                      {character.name}
                    </p>
                  </button>
                </CarouselItem>
              ))}
              <CarouselItem className="pl-2 basis-1/3 sm:basis-1/4 md:basis-1/5">
                <Link href="/characters">
                  <button className="w-full aspect-square rounded-lg border-2 border-dashed border-gray-300 hover:border-purple-400 transition-colors flex flex-col items-center justify-center">
                    <Plus className="w-6 h-6 text-gray-400 mb-1" />
                    <span className="text-xs text-gray-500">Add</span>
                  </button>
                </Link>
              </CarouselItem>
            </CarouselContent>
            <CarouselPrevious />
            <CarouselNext />
          </Carousel>
        )}

        {selectedCharacter && (
          <p className="text-sm text-gray-600 mt-4 text-center">
            <strong>{selectedCharacter.name}</strong> will appear in your story illustrations
          </p>
        )}
      </CardContent>
    </Card>
  );
}